import * as React from "react";
import { useLocation, useSearch } from "wouter";
import { Target, Plus, ChevronRight, CheckCircle2, Pause, Archive, Sparkles, FlaskConical, History, AlertCircle, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { useSpaces } from "@/hooks/useSpaces.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import type { GoalType } from "./types.js";
import { CreateGoalModal } from "./create/CreateGoalModal.js";

/** The three goal types offered at creation (routine is a separate flow). */
type CreateGoalType = "find_home" | "reorganize" | "declutter";

interface OrganizingGoal {
  id: string;
  title: string;
  description: string | null;
  activityLocationId: string | null;
  status: string;
  type: GoalType;
  userId: string;
  householdId: string;
  createdAt: string;
  updatedAt: string;
}

interface GoalTrial {
  id: string;
  goalId: string | null;
  targetSpaceId: string;
  targetSpaceName: string | null;
  status: string;
  startedAt: string | null;
  reviewAfter: string | null;
  decidedAt: string | null;
  outcomeNote: string | null;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  active: { label: "Active", color: "text-amethyst-ink bg-amethyst-soft", icon: Target },
  achieved: { label: "Achieved", color: "text-sage-deep bg-sage-wash", icon: CheckCircle2 },
  paused: { label: "Paused", color: "text-warning-text bg-warning-wash", icon: Pause },
  abandoned: { label: "Abandoned", color: "text-faint bg-clay-muted", icon: Archive },
};

export default function OrganizeGoalsPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const toast = useToast();
  const { spaces } = useSpaces();
  const { aiEnabled } = useFeatureFlags();
  const [goals, setGoals] = React.useState<OrganizingGoal[]>([]);
  const [trials, setTrials] = React.useState<GoalTrial[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [showCreate, setShowCreate] = React.useState(false);
  // Intent-first create flow (Workstream C). `createInitialType` skips the intent
  // picker and opens that type's form directly (deep links); `createInitialTitle`
  // prefills the title (e.g. from a detection nudge).
  const [createInitialType, setCreateInitialType] = React.useState<CreateGoalType | null>(null);
  const [createInitialTitle, setCreateInitialTitle] = React.useState("");

  /** Open the intent picker (no preselected type). */
  const openCreate = (initialTitle = "") => {
    setCreateInitialType(null);
    setCreateInitialTitle(initialTitle);
    setShowCreate(true);
  };

  /** Open a specific type's form directly (deep links / nudges). */
  const openCreateForType = (type: CreateGoalType, initialTitle = "") => {
    setCreateInitialType(type);
    setCreateInitialTitle(initialTitle);
    setShowCreate(true);
  };

  const fetchGoals = React.useCallback(async () => {
    setLoadError(null);
    try {
      const [goalsData, trialsData] = await Promise.all([
        apiFetch<OrganizingGoal[]>("/api/organizing-goals"),
        apiFetch<GoalTrial[]>("/api/placement-trials"),
      ]);
      setGoals(goalsData);
      setTrials(trialsData);
    } catch (err: any) {
      setLoadError(err.message || "Failed to load goals");
    }
    setLoading(false);
  }, []);

  React.useEffect(() => { fetchGoals(); }, [fetchGoals]);

  // Deep-link entry points (all clear the query param so they don't re-trigger):
  //   ?suggest_item=<id> → Find-a-home form, prefilled with the item's name
  //   ?type=<find_home|reorganize|declutter> → that type's form directly
  //   ?new=1 → the intent picker
  React.useEffect(() => {
    const params = new URLSearchParams(search);
    const suggestItemId = params.get("suggest_item");
    if (suggestItemId) {
      apiFetch<{ id: string; name: string }>(`/api/items/${suggestItemId}`)
        .then((item) => openCreateForType("find_home", `Find a home for ${item.name}`))
        .catch(() => openCreateForType("find_home", "Find a home for this item"));
      navigate("/organize/goals", { replace: true });
      return;
    }
    const typeParam = params.get("type");
    if (typeParam === "find_home" || typeParam === "reorganize" || typeParam === "declutter") {
      openCreateForType(typeParam);
      navigate("/organize/goals", { replace: true });
      return;
    }
    // ?type=<...> is handled above (opens that type's form directly); a bare ?new=1
    // opens the intent picker so the user chooses a flow.
    if (params.get("new")) {
      openCreate();
      navigate("/organize/goals", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, navigate]);

  const activeGoals = goals.filter((g) => g.status === "active");
  const completedGoals = goals.filter((g) => g.status !== "active");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-pill animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-error-wash rounded-lg border border-clay-border text-center py-16 px-6">
        <AlertCircle className="w-10 h-10 text-error mx-auto mb-3" />
        <p className="text-error-text mb-1">{loadError}</p>
        <button onClick={() => { setLoading(true); fetchGoals(); }} className="mt-3 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink flex items-center gap-2 font-headline tracking-[-0.01em]">
          <Target className="w-6 h-6 text-primary" />Goals
        </h1>
        <div className="flex items-center gap-2">
          {completedGoals.length > 0 && (
            <button
              onClick={() => navigate("/organize/goals/history")}
              className="flex items-center gap-1.5 px-3 py-2.5 text-body hover:text-ink rounded-md text-sm font-medium hover:bg-amethyst-soft transition-colors"
            >
              <History className="w-4 h-4" />History
            </button>
          )}
          <button
            onClick={() => openCreate()}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors"
          >
            <Plus className="w-4 h-4" />New Goal
          </button>
        </div>
      </div>

      {/* Detection Nudges — Phase 3 (always a find-a-home intent) */}
      <DetectionNudgesCard onCreateGoal={(title) => openCreateForType("find_home", title)} />

      {/* AI Suggested Goals — Phase 3 */}
      {aiEnabled && goals.length > 0 && (
        <AISuggestedGoalsCard onCreateGoal={(title) => openCreate(title)} />
      )}

      {/* Empty state */}
      {goals.length === 0 && (
        <div className="bg-sage-wash rounded-lg border border-clay-border p-8 text-center">
          <div className="text-4xl mb-3">🎯</div>
          <h2 className="text-[1.0625rem] font-semibold text-ink mb-2">What do you want to improve?</h2>
          <p className="text-sm text-body mb-6 max-w-sm mx-auto">
            Name something that isn't working in your home — like "I can never find the scissors" or "morning lunch packing takes too long." We'll help you try solutions.
          </p>
          <button
            onClick={() => openCreate()}
            className="px-6 py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors"
          >
            Create your first goal
          </button>
        </div>
      )}

      {/* Active goals */}
      {activeGoals.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-faint uppercase tracking-wide">Active</h2>
          {activeGoals.map((goal) => (
            <GoalCard key={goal.id} goal={goal} spaces={spaces} trials={trials.filter((t) => t.goalId === goal.id)} onClick={() => navigate(`/organize/goals/${goal.id}`)} />
          ))}
        </div>
      )}

      {/* Completed goals */}
      {completedGoals.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-faint uppercase tracking-wide">Completed</h2>
          {completedGoals.map((goal) => (
            <GoalCard key={goal.id} goal={goal} spaces={spaces} trials={trials.filter((t) => t.goalId === goal.id)} onClick={() => navigate(`/organize/goals/${goal.id}`)} />
          ))}
        </div>
      )}

      {/* Intent-first create flow */}
      <CreateGoalModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => navigate(`/organize/goals/${id}`)}
        initialType={createInitialType}
        initialTitle={createInitialTitle}
      />
    </div>
  );
}

function GoalCard({ goal, spaces, trials, onClick }: { goal: OrganizingGoal; spaces: { id: string; name: string }[]; trials: GoalTrial[]; onClick: () => void }) {
  const config = STATUS_CONFIG[goal.status] ?? STATUS_CONFIG.active;
  const Icon = config.icon;
  const locationName = goal.activityLocationId ? spaces.find((s) => s.id === goal.activityLocationId)?.name : null;

  // Trial progress info
  const activeTrials = trials.filter((t) => ["created", "confirmed_moved", "in_progress"].includes(t.status));
  const adoptedTrials = trials.filter((t) => t.status === "adopted");
  const activeTrial = activeTrials[0];
  const isOverdue = activeTrial?.reviewAfter && new Date(activeTrial.reviewAfter) < new Date();

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 rounded-lg bg-white border border-clay-border hover:border-primary/40 text-left transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${config.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm text-ink truncate">{goal.title}</div>
          <div className="text-[0.8125rem] text-faint flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-pill text-[0.8125rem] font-medium ${config.color}`}>{config.label}</span>
            {locationName && <span>📍 {locationName}</span>}
            {activeTrial && (
              <span className={`flex items-center gap-1 ${isOverdue ? "text-warning-text" : "text-primary"}`}>
                <FlaskConical className="w-3 h-3" />
                {activeTrial.status === "created" ? "Awaiting move" :
                  isOverdue ? "Check-in overdue" :
                  `Review ${new Date(activeTrial.reviewAfter!).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
              </span>
            )}
            {!activeTrial && adoptedTrials.length > 0 && (
              <span className="flex items-center gap-1 text-sage-deep">
                <CheckCircle2 className="w-3 h-3" />Last trial adopted
              </span>
            )}
          </div>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-faint flex-shrink-0" />
    </button>
  );
}


// =============================================================================
// DETECTION NUDGES — Phase 3: "Your scissors have moved 4 times..."
// =============================================================================

interface DetectionNudge {
  itemId: string;
  itemName: string;
  moveCount: number;
  currentSpace: { id: string; name: string | null } | null;
  lastMovedAt: string;
  suggestion: string;
}

function DetectionNudgesCard({ onCreateGoal }: { onCreateGoal: (title: string) => void }) {
  const [nudges, setNudges] = React.useState<DetectionNudge[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<DetectionNudge[]>("/api/organizing-goals/detection-nudges?minMoves=3&days=90");
        setNudges(data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const visibleNudges = nudges.filter((n) => !dismissed.has(n.itemId));

  if (loading || visibleNudges.length === 0) return null;

  return (
    <div className="bg-warning-wash rounded-lg border border-clay-border p-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle className="w-4 h-4 text-warning" />
        <h3 className="text-sm font-semibold text-warning-text">Items without a home</h3>
      </div>
      <div className="space-y-2">
        {visibleNudges.slice(0, 3).map((nudge) => (
          <div
            key={nudge.itemId}
            className="flex items-center justify-between p-3 rounded-md bg-white border border-clay-border"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm text-ink font-medium truncate">{nudge.itemName}</p>
              <p className="text-[0.8125rem] text-warning-text tabular-nums">
                Moved {nudge.moveCount} times in 90 days
                {nudge.currentSpace?.name && ` • now in ${nudge.currentSpace.name}`}
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
              <button
                onClick={() => onCreateGoal(`Find a home for ${nudge.itemName}`)}
                className="px-3 py-1.5 rounded-md bg-primary text-white text-[0.8125rem] font-medium hover:bg-amethyst-ink transition-colors"
              >
                Create goal
              </button>
              <button
                onClick={() => setDismissed((prev) => new Set([...prev, nudge.itemId]))}
                className="px-2 py-1.5 rounded-md text-faint text-[0.8125rem] font-medium hover:bg-clay-muted transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        ))}
      </div>
      {visibleNudges.length > 3 && (
        <p className="text-[0.8125rem] text-warning-text mt-2 text-center tabular-nums">
          + {visibleNudges.length - 3} more items moving frequently
        </p>
      )}
    </div>
  );
}

// =============================================================================
// AI SUGGESTED GOALS — Phase 3: AI-powered goal suggestions based on data
// =============================================================================

interface SuggestedGoal {
  title: string;
  description: string;
  reasoning: string;
}

function AISuggestedGoalsCard({ onCreateGoal }: { onCreateGoal: (title: string) => void }) {
  const [suggestions, setSuggestions] = React.useState<SuggestedGoal[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [fetched, setFetched] = React.useState(false);
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set());
  const toast = useToast();

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ suggestions: SuggestedGoal[] }>("/api/ai/suggest-goals", {
        method: "POST",
      });
      setSuggestions(data.suggestions);
      setFetched(true);
    } catch (err: any) {
      toast.show(err.message?.includes("limit") ? "AI action limit reached" : "Failed to get suggestions", "error");
    }
    setLoading(false);
  };

  const visibleSuggestions = suggestions.filter((s) => !dismissed.has(s.title));

  if (fetched && visibleSuggestions.length === 0) return null;

  return (
    <div className="bg-sage-wash rounded-lg border border-clay-border p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-sage-deep" />
          <h3 className="text-sm font-semibold text-sage-deep">AI-suggested goals</h3>
        </div>
        {!fetched && (
          <button
            onClick={fetchSuggestions}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sage-deep text-white text-sm font-medium hover:bg-sage-deep-hover transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {loading ? "Thinking..." : "Get suggestions"}
          </button>
        )}
        {fetched && (
          <button
            onClick={() => { setSuggestions([]); setFetched(false); setDismissed(new Set()); }}
            className="text-[0.8125rem] text-sage-deep hover:text-sage-deep-hover font-medium"
          >
            Refresh
          </button>
        )}
      </div>

      {!fetched && !loading && (
        <p className="text-sm text-body">
          Based on your inventory, spaces, and item movement patterns, AI can suggest goals tailored to your home.
        </p>
      )}

      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-sage-deep" />
        </div>
      )}

      {visibleSuggestions.length > 0 && (
        <div className="space-y-2">
          {visibleSuggestions.map((suggestion) => (
            <div
              key={suggestion.title}
              className="p-3 rounded-md bg-white border border-clay-border"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink">{suggestion.title}</p>
                  <p className="text-[0.8125rem] text-body mt-0.5">{suggestion.description}</p>
                  <p className="text-[0.8125rem] text-sage-deep mt-1 italic">{suggestion.reasoning}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => onCreateGoal(suggestion.title)}
                    className="px-3 py-1.5 rounded-md bg-sage-deep text-white text-[0.8125rem] font-medium hover:bg-sage-deep-hover transition-colors"
                  >
                    Use this
                  </button>
                  <button
                    onClick={() => setDismissed((prev) => new Set([...prev, suggestion.title]))}
                    className="px-2 py-1.5 rounded-md text-faint text-[0.8125rem] font-medium hover:bg-clay-muted transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
